import React from 'react';
import { Link } from 'react-router-dom';
import { Battery, PenTool as Tool, CreditCard, GraduationCap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ServiceCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  linkUrl: string;
}> = ({ icon, title, description, linkUrl }) => {
  return (
    <div className="bg-white rounded-xl shadow-lg transition-all duration-300 hover:shadow-xl border border-primary-100 hover:border-primary-200 group transform hover:translate-y-[-8px]">
      <div className="p-5 sm:p-6 md:p-8">
        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-primary-50 rounded-xl flex items-center justify-center text-primary-500 mb-4 sm:mb-5 md:mb-6 group-hover:bg-primary-100 transition-colors transform group-hover:scale-110">
          {icon}
        </div>
        <h3 className="text-lg sm:text-xl font-bold text-neutral-900 mb-2 sm:mb-3">{title}</h3>
        <p className="text-sm sm:text-base text-neutral-600 mb-4 sm:mb-5 md:mb-6 leading-relaxed">{description}</p>
        <Link
          to={linkUrl}
          className="text-primary-500 hover:text-primary-600 inline-flex items-center font-medium group-hover:translate-x-1 transition-transform"
        >
          En savoir plus <span className="ml-1">→</span>
        </Link>
      </div>
    </div>
  );
};

const Services: React.FC = () => {
  const services = [
    {
      icon: <Battery size={28} />,
      title: 'Stations de Recharge',
      description: 'Réseau de stations Proxy Power pour une recharge rapide et fiable de votre véhicule électrique.',
      linkUrl: '/stations',
    },
    {
      icon: <Tool size={28} />,
      title: 'Maintenance & SAV',
      description: 'Service après-vente professionnel et maintenance préventive pour garder votre moto en parfait état.',
      linkUrl: '/services#maintenance',
    },
    {
      icon: <CreditCard size={28} />,
      title: 'Leasing & Financement',
      description: 'Solutions de financement flexibles adaptées à vos besoins pour acquérir votre moto électrique.',
      linkUrl: '/pay-as-you-go',
    },
    {
      icon: <GraduationCap size={28} />,
      title: 'Proxy Academy',
      description: 'Formations professionnelles pour la conduite et la maintenance des motos électriques.',
      linkUrl: '/services#academy',
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-primary-50 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_90%_90%,_rgba(24,153,78,0.05)_0%,_transparent_50%)]"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4 sm:mb-5 md:mb-6 bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
            Nos Services
          </h2>
          <p className="text-base sm:text-lg text-neutral-600 leading-relaxed px-4">
            Des solutions complètes pour une mobilité électrique durable et accessible. Découvrez comment nous pouvons vous accompagner dans votre transition vers l'électrique.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 md:gap-8">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              linkUrl={service.linkUrl}
            />
          ))}
        </div>

        <div className="mt-10 sm:mt-12 md:mt-16 text-center">
          <Link
            to="/services"
            className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-xl transition-colors shadow-lg hover:shadow-xl transform hover:translate-y-[-2px]"
          >
            Découvrir Tous Nos Services
            <span className="ml-2">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Services;